import React, { PropsWithChildren } from 'react';
import { DialogContent, DialogTitle, IconButton, TextField } from '@mui/material';
import { Controller } from 'react-hook-form';
import { FormInputProps } from './form-input-props';
import { DataTable, DataTableColumn } from '../data-table/data-table';
import { ClassConstructor } from 'class-transformer';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { Dialog } from '../dialog/dialog';
import { Button, HStack } from '@lib/ui-components';

export interface FormInputTableMultiSelectProps extends FormInputProps {
  initialIds?: (string | number)[];
  displayFormat: string;
  dialogTitle: string;

  /** The collection the dialog lists, e.g. '/api/resource/communes'. */
  fetchUrl?: string;
  modelClass?: ClassConstructor<any>;

  fetchPostUrl?: string;
  fetchFilters?: any;
  columns: DataTableColumn[];
  selectedValueField?: string;
  onRowsClear?: () => void;
  getFormValues?: () => any;

  dependedFields?: string[];
  initialDisplayedValues?: string;
  preserveApiOrder?: boolean; // if true, preserves the order from API and disables sorting
}

export const FormInputTableMultiSelect: React.FC<FormInputTableMultiSelectProps> = (props: PropsWithChildren<FormInputTableMultiSelectProps>) => {
  const [openDialog, setOpenDialog] = React.useState(false);
  const [displayedValues, setDisplayedValues] = React.useState(props.initialDisplayedValues || '');
  const [selectedValues, setSelectedValues] = React.useState<string[]>((props.initialIds as string[]) || []);
  const { t } = useTranslation();

  // Memoised so the table callback below can depend on it. Computed fresh on every render it left the callback with
  // two bad options: stale (what it was) or rebuilt every render.
  const { fetchFilters, getFormValues } = props;
  const computedFetchFilters = React.useMemo(() => {
    const filters = fetchFilters ? { ...fetchFilters } : undefined;
    if (getFormValues && fetchFilters) {
      const formValues = getFormValues();
      Object.keys(fetchFilters).forEach((key) => {
        const paramVal = fetchFilters[key];
        if (typeof paramVal === 'string' && paramVal.startsWith('$')) {
          filters[key] = formValues[paramVal.substring(1)];
        } else {
          filters[key] = paramVal;
        }
      });
    }
    return filters;
  }, [fetchFilters, getFormValues]);

  const updateSelectedRows = React.useCallback(
    (rows: any[], onChange: (...event: any[]) => void) => {
      const selectedRows = rows.map((item) => item[props.selectedValueField || 'id']);
      setSelectedValues(selectedRows);
      onChange(selectedRows);
      const displayedValues = rows.map((item) => props.displayFormat.replace(/{([^{}]*)}/g, (match, key) => item[key] || ''));
      setDisplayedValues(displayedValues.join(', '));
    },
    [props.displayFormat, props.selectedValueField],
  );

  function handleCloseDialog() {
    setOpenDialog(false);
  }

  const dataTable = React.useCallback(
    (onChange: (...event: any[]) => void) => {
      if (props.fetchUrl) {
        return (
          <DataTable
            refreshKey={1}
            selectable={true}
            contentMaxHeightAsAlmostOneScreen={true}
            preserveApiOrder={props.preserveApiOrder}
            selectableOptions={{
              initialSelections: props.initialIds?.map((id) => ({ [props.selectedValueField || 'id']: id })),
              comparatorField: props.selectedValueField || 'id',
              onSelectedRowsChange: (rows) => updateSelectedRows(rows, onChange),
            }}
            columns={props.columns}
            fetchUrl={props.fetchUrl}
            modelClass={props.modelClass}
            fetchFilters={computedFetchFilters}
          />
        );
      }
      return (
        <DataTable
          refreshKey={1}
          selectable={true}
          contentMaxHeightAsAlmostOneScreen={true}
          preserveApiOrder={props.preserveApiOrder}
          selectableOptions={{
            initialSelections: props.initialIds?.map((id) => ({ [props.selectedValueField || 'id']: id })),
            comparatorField: props.selectedValueField || 'id',
            onSelectedRowsChange: (rows) => updateSelectedRows(rows, onChange),
          }}
          columns={props.columns}
          fetchPostUrl={props.fetchPostUrl}
          fetchFilters={computedFetchFilters}
        />
      );
    },
    // A `$field` filter resolves from the form, so without computedFetchFilters here the dialog kept whatever value
    // the filter had when this callback was last built; fetchPostUrl was ignored outright when it changed.
    [
      props.columns,
      props.modelClass,
      props.fetchUrl,
      props.fetchPostUrl,
      props.selectedValueField,
      props.initialIds,
      props.preserveApiOrder,
      computedFetchFilters,
      updateSelectedRows,
    ],
  );

  return (
    <Controller
      name={props.name}
      control={props.control}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <>
          <div data-test-id={`input-${props.name}-wrapper`}>
            <TextField
              inputProps={{ 'data-test-id': `input-${props.name}` }}
              helperText={error ? error.message : ' '}
              error={!!error}
              size='small'
              value={displayedValues}
              onChange={(event) => onChange(selectedValues)}
              onClick={() => setOpenDialog(true)}
              onKeyDown={(event) => event.preventDefault()}
              fullWidth
              label={props.label}
              variant={props.variant || 'outlined'}
              margin='normal'
              sx={{
                caretColor: 'transparent',
                cursor: 'pointer',
              }}
              disabled={props.isDeactivated}
            />
          </div>
          <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth='xl' fullWidth={true}>
            <DialogTitle>
              {props.dialogTitle}
              <IconButton
                data-test-id={'dialog-close-btn'}
                aria-label='close'
                onClick={handleCloseDialog}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              {dataTable(onChange)}
              <HStack align={'right'}>
                <Button label={t('accept')} variant={'contained'} color={'secondary'} onClick={() => handleCloseDialog()} />
              </HStack>
            </DialogContent>
          </Dialog>
        </>
      )}
    />
  );
};
